import React from 'react';
import PriorityFilter from './PriorityFilter';
import ProjectFilter from './ProjectFilter';
import RequesterFilter from './RequesterFilter';
import StatusNameFilter from './StatusNameFilter';
import DateFilter from './DateFilter';
import { LayoutStyle } from './FilterPopover';
import { UserTaskFilter } from '../types';
import { Priority } from 'models/Organization';

type Filter = {
  key: string;
  layoutStyle?: LayoutStyle;
  isFilterApplied?: boolean;
  withSearch?: boolean;
  resetParams: Record<string, any>;
  content: React.ReactNode;
};

type Props = {
  filterParams: UserTaskFilter;
  setFilterParams: (params: Record<string, any>) => void;
  organization: {
    id: string;
    statusNames: string[];
    priorities: Priority[];
  };
};

export const filters = ({
  filterParams = {},
  setFilterParams,
  organization: { id: organizationId, statusNames = [], priorities }
}: Props): Filter[] => {
  const handleFilterParams = (
    key: string,
    value: string | string[],
    checkLength?: boolean
  ) => {
    setFilterParams({
      ...filterParams,
      [key]: checkLength ? (value.length ? value : undefined) : value
    });
  };

  const {
    projectId,
    status,
    priorityId,
    requesterId,
    assignedUserId,
    createdAtStart,
    createdAtEnd,
    dueAtStart,
    dueAtEnd,
    updatedAtStart,
    updatedAtEnd
  } = filterParams;

  return [
    {
      key: 'projectId',
      resetParams: { projectId: undefined },
      content: (
        <ProjectFilter
          organizationId={organizationId}
          setSelectedProject={value =>
            handleFilterParams('projectId', value, true)
          }
          selectedProject={projectId || []}
        />
      )
    },
    {
      key: 'status',
      layoutStyle: LayoutStyle.Vertical,
      resetParams: { status: undefined },
      content: (
        <StatusNameFilter
          statusNames={[...statusNames, 'archived']}
          onChange={value => handleFilterParams('status', value, true)}
          value={status || []}
        />
      )
    },
    {
      key: 'priorityId',
      layoutStyle: LayoutStyle.Vertical,
      resetParams: { priorityId: undefined },
      content: (
        <PriorityFilter
          priorities={priorities}
          onChange={value => handleFilterParams('priorityId', value, true)}
          value={priorityId || []}
        />
      )
    },
    {
      key: 'requesterId',
      resetParams: { requesterId: undefined },
      content: (
        <RequesterFilter
          organizationId={organizationId}
          onChange={value => handleFilterParams('requesterId', value, true)}
          value={requesterId || []}
        />
      )
    },
    {
      key: 'assignedUserId',
      resetParams: { assignedUserId: undefined },
      content: (
        <RequesterFilter
          organizationId={organizationId}
          onChange={value => handleFilterParams('assignedUserId', value, true)}
          value={assignedUserId || []}
        />
      )
    },
    {
      key: 'createdAt',
      isFilterApplied: !!createdAtStart || !!createdAtEnd,
      withSearch: true,
      resetParams: { createdAtStart: undefined, createdAtEnd: undefined },
      content: (
        <DateFilter
          setDateStart={value => handleFilterParams('createdAtStart', value)}
          setDateEnd={value => handleFilterParams('createdAtEnd', value)}
          value={[createdAtStart, createdAtEnd]}
        />
      )
    },
    {
      key: 'dueAt',
      isFilterApplied: !!dueAtStart || !!dueAtEnd,
      withSearch: true,
      resetParams: { dueAtStart: undefined, dueAtEnd: undefined },
      content: (
        <DateFilter
          setDateStart={value => handleFilterParams('dueAtStart', value)}
          setDateEnd={value => handleFilterParams('dueAtEnd', value)}
          value={[dueAtStart, dueAtEnd]}
        />
      )
    },
    {
      key: 'updatedAt',
      isFilterApplied: !!updatedAtStart || !!updatedAtEnd,
      withSearch: true,
      resetParams: {
        updatedAtStart: undefined,
        updatedAtEnd: undefined
      },
      content: (
        <DateFilter
          setDateStart={value => handleFilterParams('updatedAtStart', value)}
          setDateEnd={value => handleFilterParams('updatedAtEnd', value)}
          value={[updatedAtStart, updatedAtEnd]}
        />
      )
    }
  ];
};
